import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import deepEqual from 'fast-deep-equal';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { Form, Input } from '../../ui';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import { logout } from '../../../redux/authentication/slice';
import { updateConfigOnboardingSettings } from '../../../redux/config/slice';
import { updateOrganization } from '../../../redux/organizations/slice';
import { selectCurrentOrganization } from '../../../redux/authentication/selectors';
import { selectUpdateOrganizationLoading } from '../../../redux/organizations/selectors';
import { selectUpdateConfigLoading } from '../../../redux/config/selectors';
import { DEFAULT_RULES } from '../../../consts/formRules';
import { ONBOARDING_STEPS } from '../../../consts/consts';
import {
  OnboardingStepWrapper,
  OnboardingContent,
  OnboardingHeaderGroup,
  OnboardingContentGroup,
  OnboardingContentTitle,
  OnboardingContentInfo,
  OnboardingContentGroupText
} from '../../ui/Layout/OnboardingLayout';
import { Heading2 } from '../../../components/ui/Typography/Heading';

const OnboardingStepOrganizationComponent = ({
  currentOrganization,
  updateOrganizationLoading,
  updateOrganization,
  updateConfigOnboardingSettings,
  updateConfigLoading,
  isInitial = false,
  logout
}) => {
  const [form] = Form.useForm();
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);

  const initialFormData = {
    name: isInitial ? '' : currentOrganization?.name || '',
    email: isInitial ? '' : currentOrganization?.email || ''
  };

  useEffect(() => {
    if (currentOrganization) {
      form.setFieldsValue(initialFormData);

      if (!isInitial) {
        setIsFieldsTouched(true);
      }
    }
  }, [currentOrganization, isInitial]);

  const isFormDataEqual = () => {
    if (!isFieldsTouched) {
      return true;
    }
    const currentData = form.getFieldsValue();
    return deepEqual(currentData, initialFormData);
  };

  const handleSaveOrganization = (callback) => {
    form
      .validateFields()
      .then((values) => {
        if (isFormDataEqual()) {
          callback?.();
          return;
        }

        updateOrganization({
          organization: { ...currentOrganization, ...values },
          callback,
          renderSuccess: false
        });
      })
      .catch(() => {});
  };

  const handleSaveAndLogout = () => {
    const hasChanges = !isFormDataEqual();

    if (!hasChanges) {
      logout();
      return;
    }

    form
      .validateFields()
      .then((values) => {
        updateOrganization({
          organization: { ...currentOrganization, ...values },
          callback: () => {
            updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.ORG_EDIT });
            logout();
          },
          renderSuccess: false
        });
      })
      .catch(() => logout());
  };

  const handleNext = () => {
    handleSaveOrganization(() => {
      updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.APPEARANCE });
    });
  };

  return (
    <>
      <OnboardingNavbar onLogout={handleSaveAndLogout} loading={updateOrganizationLoading || updateConfigLoading} />
      <OnboardingProgress />
      <OnboardingContent className="step-organization">
        <OnboardingStepWrapper>
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Angaben zu Ihrem Unternehmen</Heading2>
            </OnboardingContentTitle>
            <OnboardingContentInfo>
              <OnboardingContentGroupText>
                Hinterlegen Sie den Namen Ihres Unternehmens und eine zentrale E-Mail, auf die alle relevanten Personen
                Zugriff haben (z. B. info@... oder service@...). <br /> So kommen Benachrichtigungen zu Schaden- und
                Risikoänderungen, sowie neue Nachrichten zuverlässig bei Ihnen an.
              </OnboardingContentGroupText>
            </OnboardingContentInfo>
          </OnboardingHeaderGroup>

          <OnboardingContentGroup className="step-organization">
            <Form form={form} layout="vertical" onValuesChange={() => setIsFieldsTouched(Math.random())}>
              <Form.Item label="Name" name="name" rules={[...DEFAULT_RULES.INPUT_REQUIRED, ...DEFAULT_RULES.NO_COMMA]}>
                <Input />
              </Form.Item>

              <Form.Item label="E-Mail" name="email" rules={DEFAULT_RULES.EMAIL} validateTrigger="onChange">
                <Input />
              </Form.Item>
            </Form>
          </OnboardingContentGroup>
        </OnboardingStepWrapper>

        <OnboardingActions onNext={handleNext} loading={updateOrganizationLoading || updateConfigLoading} />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  currentOrganization: selectCurrentOrganization(state),
  updateOrganizationLoading: selectUpdateOrganizationLoading(state),
  updateConfigLoading: selectUpdateConfigLoading(state)
});

const mapDispatchToProps = {
  updateOrganization,
  updateConfigOnboardingSettings,
  logout
};

export const OnboardingStepOrganization = connect(
  mapStateToProps,
  mapDispatchToProps
)(OnboardingStepOrganizationComponent);
